import { z } from 'zod'
import { Id, Timestamp } from './common'

const clicks = z.number().int().min(0).max(60)

/** Suspension settings for a fork or shock. Pressure in bar (SI-style storage). */
export const SuspensionSettings = z
  .object({
    pressure_bar: z.number().min(0).max(25),
    sag_pct: z.number().min(0).max(60),
    rebound: clicks,
    hsc: clicks,
    lsc: clicks,
    spacers: z.number().int().min(0).max(10),
    /** Coil spring rate, e.g. "450 lb/in", kept as written on the spring. */
    spring: z.string().trim().max(40),
  })
  .partial()
export type SuspensionSettings = z.infer<typeof SuspensionSettings>

export const TyreSettings = z
  .object({
    front_bar: z.number().min(0).max(12),
    rear_bar: z.number().min(0).max(12),
    front_insert: z.boolean(),
    rear_insert: z.boolean(),
  })
  .partial()
export type TyreSettings = z.infer<typeof TyreSettings>

export const SetupSettings = z.strictObject({
  fork: SuspensionSettings.optional(),
  shock: SuspensionSettings.optional(),
  tyres: TyreSettings.optional(),
  notes: z.string().trim().max(1000).optional(),
})
export type SetupSettings = z.infer<typeof SetupSettings>

export const SetupSnapshot = z.object({
  id: Id,
  bike_id: Id,
  taken_at: Timestamp,
  label: z.string().nullable(),
  conditions: z.string().nullable(),
  settings: SetupSettings,
  created_at: Timestamp,
})
export type SetupSnapshot = z.infer<typeof SetupSnapshot>

export const SetupList = z.object({ setups: z.array(SetupSnapshot) })

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional()

export const CreateSetupBody = z.object({
  taken_at: Timestamp.optional(),
  label: text(120),
  conditions: text(200),
  settings: SetupSettings,
})
export type CreateSetupBody = z.input<typeof CreateSetupBody>

export const UpdateSetupBody = CreateSetupBody.partial().refine(
  (b) => Object.keys(b).length > 0,
  'Nothing to update.',
)
export type UpdateSetupBody = z.input<typeof UpdateSetupBody>

export const BikeSetupParams = z.object({ id: Id, setupId: Id })
